import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  Plus,
  Users,
  MoreVertical,
  Pencil,
  Trash2,
  DollarSign,
  Percent,
  Loader2,
  TrendingUp,
  Wallet,
} from "lucide-react";
import { API } from "@/App";

export default function Sellers({ user }) {
  const [sellers, setSellers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSeller, setEditingSeller] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [payoutDialogOpen, setPayoutDialogOpen] = useState(false);
  const [payoutSeller, setPayoutSeller] = useState(null);
  const [payoutAmount, setPayoutAmount] = useState("");

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    id_number: "",
    territory: "",
    notes: "",
    commission_percent: "15",
    joined_at: "",
  });

  useEffect(() => {
    fetchSellers();
  }, []);

  const fetchSellers = async () => {
    try {
      const response = await fetch(`${API}/sellers`, { credentials: "include" });
      if (response.ok) {
        setSellers(await response.json());
      }
    } catch (error) {
      toast.error("Failed to fetch sellers");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name) {
      toast.error("Seller name is required");
      return;
    }

    setSaving(true);
    try {
      const url = editingSeller
        ? `${API}/sellers/${editingSeller.seller_id}`
        : `${API}/sellers`;
      const method = editingSeller ? "PUT" : "POST";

      const response = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...form,
          commission_percent: parseFloat(form.commission_percent) || 15,
        }),
        credentials: "include",
      });

      if (response.ok) {
        toast.success(editingSeller ? "Seller updated" : "Seller created");
        setDialogOpen(false);
        setEditingSeller(null);
        setForm({ name: "", email: "", phone: "", address: "", id_number: "", territory: "", notes: "", commission_percent: "15", joined_at: "" });
        fetchSellers();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to save seller");
      }
    } catch (error) {
      toast.error("Error saving seller");
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (seller) => {
    setEditingSeller(seller);
    setForm({
      name: seller.name || "",
      email: seller.email || "",
      phone: seller.phone || "",
      address: seller.address || "",
      id_number: seller.id_number || "",
      territory: seller.territory || "",
      notes: seller.notes || "",
      commission_percent: seller.commission_percent?.toString() || "15",
      joined_at: seller.joined_at || "",
    });
    setDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      const response = await fetch(`${API}/sellers/${deleteId}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (response.ok) {
        toast.success("Seller deleted");
        setSellers(sellers.filter((s) => s.seller_id !== deleteId));
      } else {
        toast.error("Failed to delete seller");
      }
    } catch (error) {
      toast.error("Error deleting seller");
    }
    setDeleteId(null);
  };

  const handlePayout = async () => {
    if (!payoutSeller || !payoutAmount) return;

    try {
      const response = await fetch(`${API}/payouts`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          seller_id: payoutSeller.seller_id,
          amount: parseFloat(payoutAmount),
          payment_method: "cash",
        }),
        credentials: "include",
      });

      if (response.ok) {
        toast.success("Payout recorded");
        setPayoutDialogOpen(false);
        setPayoutSeller(null);
        setPayoutAmount("");
        fetchSellers();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Payout failed");
      }
    } catch (error) {
      toast.error("Error recording payout");
    }
  };

  const openPayoutDialog = (seller) => {
    setPayoutSeller(seller);
    setPayoutAmount(seller.balance_due?.toString() || "0");
    setPayoutDialogOpen(true);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(value || 0);
  };

  return (
    <Layout user={user}>
      <div className="space-y-6" data-testid="sellers-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">
              Sellers
            </h1>
            <p className="text-muted-foreground">
              Manage sales representatives and commissions
            </p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) {
              setEditingSeller(null);
              setForm({ name: "", email: "", phone: "", address: "", id_number: "", territory: "", notes: "", commission_percent: "15", joined_at: "" });
            }
          }}>
            <DialogTrigger asChild>
              <Button className="rounded-sm" data-testid="add-seller-btn">
                <Plus className="h-4 w-4 mr-2" />
                Add Seller
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingSeller ? "Edit Seller" : "Add New Seller"}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Full Name *</Label>
                  <Input
                    id="name"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="Full legal name"
                    className="rounded-sm"
                    required
                    data-testid="seller-name-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      placeholder="email@example.com"
                      className="rounded-sm"
                      data-testid="seller-email-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone</Label>
                    <Input
                      id="phone"
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      placeholder="555-1234"
                      className="rounded-sm font-data"
                      data-testid="seller-phone-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="address">Address</Label>
                  <Input
                    id="address"
                    value={form.address}
                    onChange={(e) => setForm({ ...form, address: e.target.value })}
                    placeholder="Street address, city, state"
                    className="rounded-sm"
                    data-testid="seller-address-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="id_number">ID / License Number</Label>
                    <Input
                      id="id_number"
                      value={form.id_number}
                      onChange={(e) => setForm({ ...form, id_number: e.target.value })}
                      placeholder="Gov ID or license #"
                      className="rounded-sm font-data"
                      data-testid="seller-id-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="territory">Territory / Region</Label>
                    <Input
                      id="territory"
                      value={form.territory}
                      onChange={(e) => setForm({ ...form, territory: e.target.value })}
                      placeholder="e.g. Downtown, West Side"
                      className="rounded-sm"
                      data-testid="seller-territory-input"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="commission">Commission %</Label>
                    <div className="relative">
                      <Input
                        id="commission"
                        type="number"
                        min="0"
                        max="100"
                        step="0.5"
                        value={form.commission_percent}
                        onChange={(e) => setForm({ ...form, commission_percent: e.target.value })}
                        className="rounded-sm font-data pr-8"
                        data-testid="commission-input"
                      />
                      <Percent className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    </div>
                    <p className="text-xs text-muted-foreground">
                      % of profit per sale
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="joined_at">Start Date</Label>
                    <Input
                      id="joined_at"
                      type="date"
                      value={form.joined_at}
                      onChange={(e) => setForm({ ...form, joined_at: e.target.value })}
                      className="rounded-sm font-data"
                      data-testid="seller-joined-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="notes">Notes</Label>
                  <Input
                    id="notes"
                    value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    placeholder="Additional notes about this seller"
                    className="rounded-sm"
                    data-testid="seller-notes-input"
                  />
                </div>
                <div className="flex gap-2 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setDialogOpen(false)}
                    className="rounded-sm"
                  >
                    Cancel
                  </Button>
                  <Button type="submit" disabled={saving} className="rounded-sm flex-1">
                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (editingSeller ? "Update" : "Create")}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Card className="rounded-sm border">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="h-10 w-10 rounded-sm bg-blue-500/10 flex items-center justify-center">
                <Users className="h-5 w-5 text-blue-500" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase">Active Sellers</p>
                <p className="font-data text-2xl font-semibold">
                  {sellers.filter(s => s.status === "active").length}
                </p>
              </div>
            </CardContent>
          </Card>
          <Card className="rounded-sm border">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="h-10 w-10 rounded-sm bg-emerald-500/10 flex items-center justify-center">
                <TrendingUp className="h-5 w-5 text-emerald-500" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase">Total Sales</p>
                <p className="font-data text-2xl font-semibold">
                  {formatCurrency(sellers.reduce((sum, s) => sum + (s.total_sales || 0), 0))}
                </p>
              </div>
            </CardContent>
          </Card>
          <Card className="rounded-sm border">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="h-10 w-10 rounded-sm bg-orange-500/10 flex items-center justify-center">
                <Wallet className="h-5 w-5 text-orange-500" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase">Unpaid Commission</p>
                <p className="font-data text-2xl font-semibold text-orange-500">
                  {formatCurrency(sellers.reduce((sum, s) => sum + (s.balance_due || 0), 0))}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Sellers Table */}
        <Card className="rounded-sm border">
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 space-y-3">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="h-16 skeleton-pulse" />
                ))}
              </div>
            ) : sellers.length === 0 ? (
              <div className="p-8 text-center">
                <Users className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                <p className="text-muted-foreground mb-4">No sellers yet</p>
                <Button onClick={() => setDialogOpen(true)} className="rounded-sm">
                  Add your first seller
                </Button>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Seller</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead>Territory</TableHead>
                    <TableHead className="text-center">Commission</TableHead>
                    <TableHead className="font-data text-right">Total Sales</TableHead>
                    <TableHead className="font-data text-right">Earned</TableHead>
                    <TableHead className="font-data text-right">Balance Due</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="w-12"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sellers.map((seller) => (
                    <TableRow key={seller.seller_id} className="data-row">
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                            <span className="text-primary font-semibold">
                              {seller.name?.charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <div>
                            <p className="font-medium">{seller.name}</p>
                            {seller.id_number && <p className="text-xs text-muted-foreground font-data">ID: {seller.id_number}</p>}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">
                          {seller.email && <p>{seller.email}</p>}
                          {seller.phone && <p className="text-muted-foreground font-data">{seller.phone}</p>}
                          {!seller.email && !seller.phone && <span className="text-muted-foreground">-</span>}
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm">{seller.territory || <span className="text-muted-foreground">-</span>}</span>
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge variant="secondary" className="rounded-sm font-data">
                          {seller.commission_percent}%
                        </Badge>
                      </TableCell>
                      <TableCell className="font-data text-right">
                        {formatCurrency(seller.total_sales)}
                      </TableCell>
                      <TableCell className="font-data text-right text-emerald-500">
                        {formatCurrency(seller.total_commission)}
                      </TableCell>
                      <TableCell className="font-data text-right">
                        {seller.balance_due > 0 ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openPayoutDialog(seller)}
                            className="text-orange-500 hover:text-orange-600 font-data"
                          >
                            {formatCurrency(seller.balance_due)}
                          </Button>
                        ) : (
                          <span className="text-muted-foreground">$0.00</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={seller.status === "active" ? "default" : "secondary"}
                          className="rounded-sm"
                        >
                          {seller.status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleEdit(seller)}>
                              <Pencil className="h-4 w-4 mr-2" />
                              Edit
                            </DropdownMenuItem>
                            {seller.balance_due > 0 && (
                              <DropdownMenuItem onClick={() => openPayoutDialog(seller)}>
                                <DollarSign className="h-4 w-4 mr-2" />
                                Pay Commission
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem
                              className="text-destructive"
                              onClick={() => setDeleteId(seller.seller_id)}
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Delete Dialog */}
        <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Seller</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete this seller? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground">
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Payout Dialog */}
        <Dialog open={payoutDialogOpen} onOpenChange={setPayoutDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Pay Commission</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="p-4 bg-muted rounded-sm">
                <p className="text-sm text-muted-foreground">Paying to</p>
                <p className="font-medium">{payoutSeller?.name}</p>
                <p className="text-sm text-muted-foreground mt-2">Balance Due</p>
                <p className="font-data text-xl font-semibold text-orange-500">
                  {formatCurrency(payoutSeller?.balance_due)}
                </p>
              </div>
              <div className="space-y-2">
                <Label>Payout Amount</Label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    type="number"
                    step="0.01"
                    value={payoutAmount}
                    onChange={(e) => setPayoutAmount(e.target.value)}
                    className="rounded-sm font-data pl-8"
                    max={payoutSeller?.balance_due}
                  />
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => setPayoutDialogOpen(false)}
                  className="rounded-sm"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handlePayout}
                  className="rounded-sm flex-1"
                  disabled={!payoutAmount || parseFloat(payoutAmount) <= 0}
                >
                  Record Payout
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
